// ═══════════════════════════════════════════════════════════════════════════
//  useContractLtps — the live price of the CONTRACT each open row holds
//  (God Phase 3, 2026-09-24): the option premium, the futures contract, the
//  stock — never the index spot.
//
//  Three lanes, in this order, all reading the SERVER, never the broker
//  (RULE #22 — the browser is a cache reader):
//
//   1. WS   `/ws/live-feed` via lib/marketFeed — ONE ref-counted socket for the
//           whole app (the same push SuperChart panes ride). The spine already
//           broadcasts every captured option strike as NIFTY_23250_CE-style
//           keys; subscribing here adds zero upstream load.
//   2. CHAIN `GET /options/chain?symbol=<underlying>` — publicly proxied, backend
//           cache 3 s, ONE call per UNDERLYING (never per contract), and only
//           for underlyings that still have an open OPTION row with no WS tick
//           in the last 10 s. Polled every 5 s, paused while the tab is hidden.
//           (`/option-ltp` and `/option-quote` are NOT reachable through the
//           public gateway — measured 2026-09-24: "Cannot GET /option-ltp".)
//   3. BRIDGE the caller's `/bridge/rag/positions` leg prices, already polled
//           by the page at 5 s — used only when neither lane above has a price.
//
//  A price is never invented: an id with no source stays absent and the UI
//  renders "—" with the reason.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from 'react';
import { subscribeLTP, getLastLTP } from '../lib/marketFeed';
import { API_BASE } from '../lib/api';
import { ocOptionBase } from '../components/SuperChart/optionChainUtils';
import { contractParts, feedKeysOf, type AssetKind, type PnlRow } from './pnlMath';

export interface LtpTarget {
  id: string;
  kind: AssetKind;
  /** Underlying in ledger spelling ("NIFTY_50"). */
  underlying: string;
  strike: number;
  optType: string;
  /** Contract expiry (YYYY-MM-DD). The chain lane prices ONLY this expiry - a strike alone names a family. */
  expiry?: string;
  /** Symbols to subscribe on the shared feed (pnlMath.feedKeysOf). */
  feedKeys: string[];
  /** Broker contract id (ledger securityId) - the only key that names ONE contract. */
  securityId?: string;
}

export type LtpSource = 'contract' | 'ws' | 'chain' | 'bridge';
export interface LiveLtp { ltp: number; at: number; source: LtpSource }

const CONTRACT_EVERY = 1_000;   // /options/contract-ltp is an in-memory read (zero broker calls)
const WS_FRESH_MS   = 10_000;   // a WS tick younger than this beats the chain
const CHAIN_EVERY   = 5_000;    // matches foundation.OPTION_CHAIN_INTERVAL (cache is 3 s)
const PUBLISH_MS    = 400;      // coalesce bursts of ticks into one render

/** Same normalisation lib/marketFeed applies, so the chain lane and the WS
 *  lane key their answers identically. */
const normKey = (s: string) =>
  String(s || '').toUpperCase().replace(/[\s_]/g, '').replace('NIFTY50', 'NIFTY');

export function useContractLtps(
  targets: LtpTarget[],
  bridge?: Record<string, { ltp: number | null } | undefined>,
): Record<string, LiveLtp> {
  const [tick, setTick] = useState(0);
  const store = useRef<Record<string, LiveLtp>>({});
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const publish = () => {
    if (pendingRef.current) return;
    pendingRef.current = setTimeout(() => { pendingRef.current = null; setTick(t => t + 1); }, PUBLISH_MS);
  };

  // Identity of the subscription set — ids and their keys, order-independent.
  const sig = targets.map(t => `${t.id}=${t.feedKeys.map(normKey).join('/')}`).sort().join('|');

  useEffect(() => {
    if (!targets.length) { store.current = {}; setTick(t => t + 1); return; }
    let alive = true;
    // Drop ids that left the set so a closed leg's price cannot linger.
    const live = new Set(targets.map(t => t.id));
    for (const k of Object.keys(store.current)) if (!live.has(k)) delete store.current[k];

    // ── 0. CONTRACT lane (God 2026-10-26 ~09:2x: "id ke bahar jaake trade price na dikhe") ─
    // An option row that carries its securityId is priced ONLY by that id: GET /options/contract-ltp (the API's
    // in-memory option stream, zero broker calls, <= 200 ids per call, one call for every open row), 1 s cadence.
    // Measured 26-Oct 09:21: BANK_NIFTY 53900 PE 23-Nov (id 62132) showed 314.40 / -15,458 M2M because the WS
    // carries "BANKNIFTY_53900_PE" (front month 27-Oct, 336) AND "BANK_NIFTY_53900_PE" (id 62132, 885); both
    // normalise to BANKNIFTY53900PE, so the row flipped between the two contracts on every tick. Such a row never
    // joins the WS or the chain lane - when its id has no price the bridge (also securityId-keyed) or "—" shows.
    const byContract = targets.filter(t => t.kind === 'OPTION' && t.securityId);
    const contractIds = new Set(byContract.map(t => t.id));
    const pullContracts = async () => {
      if (!alive || document.hidden || !byContract.length) return;
      try {
        const ids = Array.from(new Set(byContract.map(t => String(t.securityId)))).slice(0, 200).join(',');
        const r = await fetch(`${API_BASE}/options/contract-ltp?ids=${encodeURIComponent(ids)}&t=${Date.now()}`,
          { cache: 'no-store', signal: AbortSignal.timeout(4000) });
        if (!r.ok) return;
        const d = await r.json();
        if (!alive) return;
        const at = Date.now();
        let changed = false;
        for (const t of byContract) {
          const c = d?.contracts?.[String(t.securityId)];
          const ltp = Number(c?.ltp);
          // the stream names the expiry of the id it priced - refuse anything but the row's own contract
          if (!(ltp > 0) || (t.expiry && c?.expiry && String(c.expiry) !== t.expiry)) { delete store.current[t.id]; changed = true; continue; }
          const cur = store.current[t.id];
          if (cur && cur.ltp === ltp && cur.source === 'contract') continue;
          store.current[t.id] = { ltp, at, source: 'contract' };
          changed = true;
        }
        if (changed) publish();
      } catch { /* next cycle */ }
    };
    void pullContracts();
    const ivContract = byContract.length ? setInterval(pullContracts, CONTRACT_EVERY) : null;

    // ── 1. WS lane ────────────────────────────────────────────────────────
    // Option keys on the feed carry no expiry ("BANKNIFTY_53900_PE" is the nearest contract only), so an option row
    // that names its expiry is never priced from them - it goes to the contract or the chain lane.
    const idsByKey = new Map<string, string[]>();
    for (const t of targets) for (const k of (contractIds.has(t.id) || (t.kind === 'OPTION' && t.expiry) ? [] : t.feedKeys)) {
      const nk = normKey(k);
      if (!nk) continue;
      (idsByKey.get(nk) || idsByKey.set(nk, []).get(nk)!).push(t.id);
      // cache-warm: a tick another pane already received is still a real tick
      const c = getLastLTP(k);
      if (c && Date.now() - c.at < WS_FRESH_MS) {
        store.current[t.id] = { ltp: c.ltp, at: c.at, source: 'ws' };
      }
    }
    const unsubs: (() => void)[] = [];
    for (const [nk, ids] of idsByKey) {
      unsubs.push(subscribeLTP(nk, (ltp, at) => {
        if (!alive || !(ltp > 0)) return;
        for (const id of ids) store.current[id] = { ltp, at, source: 'ws' };
        publish();
      }));
    }
    publish();

    // ── 2. CHAIN lane (options only, per underlying, only when WS is silent) ─
    const inflight = new Set<string>();
    const pullChain = async () => {
      if (!alive || document.hidden) return;
      const now = Date.now();
      const need = new Map<string, LtpTarget[]>();
      for (const t of targets) {
        if (t.kind !== 'OPTION' || !t.strike || !t.optType || contractIds.has(t.id)) continue;
        const cur = store.current[t.id];
        if (cur && cur.source === 'ws' && now - cur.at < WS_FRESH_MS) continue;
        // God 2026-09-29: one request per (underlying x EXPIRY). Keyed by underlying alone, BANK_NIFTY 54300 CE
        // 27-Oct was priced from the 29-Sep contract (156.25 vs 1,180 entry = -30,713 M2M on screen).
        if (!t.expiry) continue;
        const u = `${ocOptionBase(t.underlying)}|${t.expiry}`;
        (need.get(u) || need.set(u, []).get(u)!).push(t);
      }
      for (const [u, list] of need) {
        if (inflight.has(u)) continue;
        inflight.add(u);
        try {
          const [uSym, uExp] = u.split('|');
          const r = await fetch(`${API_BASE}/options/chain?symbol=${encodeURIComponent(uSym)}&expiry=${encodeURIComponent(uExp)}&t=${now}`,
            { cache: 'no-store', signal: AbortSignal.timeout(8000) });
          if (!r.ok) continue;
          const d = await r.json();
          if (!alive) return;
          // the server falls back to the front month when the asked expiry is not loaded - never price on that
          if (String(d?.servedExpiry || '') !== uExp) continue;
          const rows: any[] = Array.isArray(d?.data) ? d.data : [];
          const at = Date.now();
          let changed = false;
          for (const t of list) {
            const row = rows.find(x => Number(x?.strikePrice) === Number(t.strike));
            const leg = row?.[t.optType === 'PE' ? 'PE' : 'CE'];
            const ltp = Number(leg?.ltp);
            if (!(ltp > 0)) continue;
            const cur = store.current[t.id];
            if (cur && cur.source === 'ws' && at - cur.at < WS_FRESH_MS) continue;   // WS woke up meanwhile
            store.current[t.id] = { ltp, at, source: 'chain' };
            changed = true;
          }
          if (changed) publish();
        } catch { /* next cycle */ }
        finally { inflight.delete(u); }
      }
    };
    void pullChain();
    const iv = setInterval(pullChain, CHAIN_EVERY);

    return () => {
      alive = false;
      clearInterval(iv);
      if (ivContract) clearInterval(ivContract);
      unsubs.forEach(u => { try { u(); } catch { /* torn down */ } });
      if (pendingRef.current) { clearTimeout(pendingRef.current); pendingRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // ── 3. BRIDGE lane merged at read time — lowest priority, never stored ──
  return useMemo(() => {
    const out: Record<string, LiveLtp> = { ...store.current };
    if (bridge) {
      for (const t of targets) {
        if (out[t.id]) continue;
        const b = bridge[t.id];
        if (b && b.ltp != null && Number(b.ltp) > 0) out[t.id] = { ltp: Number(b.ltp), at: Date.now(), source: 'bridge' };
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, sig, bridge]);
}

/** Why a row has no live price — for the "—" tooltip. */
export function noLtpReason(kind: AssetKind, hasStrike: boolean): string {
  if (kind === 'OPTION' && !hasStrike) return 'no live tick — row carries no strike to look up';
  if (kind === 'OPTION') return 'no live tick — contract not in the WS ladder and not in the option chain yet';
  if (kind === 'FUTURE') return 'no live tick — futures contract not on the live feed';
  return 'no live tick — symbol not on the live feed';
}

/** Build the hook's target for one ledger row (open positions, open legs). */
export function ltpTargetOf(row: PnlRow): LtpTarget {
  const p = contractParts(row);
  return {
    id: String(row.id || ''), kind: p.kind, underlying: p.underlyingKey,
    strike: p.strike, optType: p.optType, expiry: p.expiry, feedKeys: feedKeysOf(row),
    securityId: row.securityId != null && String(row.securityId).trim() && Number(row.securityId) > 0 ? String(row.securityId).trim() : undefined,
  };
}
